// Scroll reveal: items rise from below into their resting position as they enter
// the viewport (effects.css holds the styles). Stagger modes:
//   'batch' — items revealed by the same observer tick cascade in visual order
//             (top-to-bottom, left-to-right); suits mixed-span grids.
//   'order' — each item's delay is fixed by its DOM order in the matched list, so a
//             row always cascades first-to-last no matter how it enters the viewport.

const STAGGER_MS = 90;

type StaggerMode = 'batch' | 'order';

export function initScrollReveal(
  selector = '.features__grid .feature',
  stagger: StaggerMode = 'batch',
): void {
  const items = [...document.querySelectorAll<HTMLElement>(selector)];
  if (items.length === 0 || !('IntersectionObserver' in window)) return;

  const orderDelay = new Map(items.map((el, i) => [el, i * STAGGER_MS]));

  const observer = new IntersectionObserver(
    (entries) => {
      const incoming = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => {
          const ra = a.boundingClientRect;
          const rb = b.boundingClientRect;
          return ra.top - rb.top || ra.left - rb.left;
        });
      for (const [i, entry] of incoming.entries()) {
        const el = entry.target as HTMLElement;
        const delay = stagger === 'order' ? (orderDelay.get(el) ?? 0) : i * STAGGER_MS;
        el.style.transitionDelay = `${delay}ms`;
        el.addEventListener(
          'transitionend',
          () => {
            el.style.transitionDelay = '';
          },
          { once: true },
        );
        el.classList.add('reveal--in');
        observer.unobserve(el);
      }
    },
    { threshold: 0.2, rootMargin: '0px 0px -5% 0px' },
  );

  for (const el of items) {
    el.classList.add('reveal');
    observer.observe(el);
  }
}
